/**
 * Canvas pixels per metre of panel or button: docs/04 asks for at least 1024,
 * so text stays sharp in the headset (render/panel.ts draws at this).
 */
export const PX_PER_METRE = 1024;

/** The smallest letters on a button, in canvas pixels. */
export const BUTTON_MIN_FONT = 18;

/**
 * How a button's label is fitted to a button `width` by `height` canvas
 * pixels: bold, starting at 40% of its height, wrapped within 86% of its width
 * and 72% of its height, shrinking to no less than 18 px (render/panel.ts).
 */
export function buttonTextOptions(
  width: number,
  height: number,
): {
  maxWidth: number;
  maxHeight: number;
  fontSize: number;
  minFontSize: number;
  bold: true;
} {
  return {
    maxWidth: width * 0.86,
    maxHeight: height * 0.72,
    fontSize: height * 0.4,
    minFontSize: BUTTON_MIN_FONT,
    bold: true,
  };
}

/** Measures a string at a given font size, in pixels. */
export type MeasureText = (text: string, fontSize: number, bold: boolean) => number;

export type FittedText = {
  readonly lines: readonly string[];
  readonly fontSize: number;
};

/**
 * Wraps text to a width, shrinking the font until it also fits the height.
 *
 * docs/04-lesson-format.md requires that titles and body auto-shrink to fit.
 * Only the AR prototype did this; the VR one wrapped at a fixed size and let
 * long Dutch words overflow the canvas. Dutch is the reason this matters:
 * compounds like "zwaartekracht" do not break.
 *
 * Kept free of canvas so it can be tested: the caller supplies `measure`.
 */
export function fitText(
  text: string,
  options: {
    maxWidth: number;
    maxHeight: number;
    fontSize: number;
    minFontSize?: number;
    lineHeight?: number;
    bold?: boolean;
  },
  measure: MeasureText,
): FittedText {
  const { maxWidth, maxHeight, fontSize } = options;
  const minFontSize = options.minFontSize ?? 12;
  const lineHeight = options.lineHeight ?? 1.3;
  const bold = options.bold ?? false;

  let size = fontSize;
  let lines = wrap(text, maxWidth, size, bold, measure);

  while (size > minFontSize && lines.length * size * lineHeight > maxHeight) {
    // Two pixels at a time, but never past the smallest size.
    size = Math.max(minFontSize, size - 2);
    lines = wrap(text, maxWidth, size, bold, measure);
  }

  return { lines, fontSize: size };
}

function wrap(
  text: string,
  maxWidth: number,
  fontSize: number,
  bold: boolean,
  measure: MeasureText,
): string[] {
  const lines: string[] = [];
  let line = '';

  for (const word of text.split(/\s+/).filter(Boolean)) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && measure(candidate, fontSize, bold) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);

  // A single word wider than the line still has to go somewhere; the caller's
  // shrink loop deals with it, and at minimum font size it overflows slightly
  // rather than disappearing.
  return lines.length > 0 ? lines : [''];
}

/**
 * A label's canvas (render/panel.ts `makeLabel`): 512 px wide with 480 for text, a line 128 px high, letters
 * from 64 px down to 28. A direction badge's label (`wide`) is 1.5 times as wide, for its two lines.
 */
export const LABEL_TEXT = {
  width: 512,
  maxWidth: 480,
  lineHeight: 128,
  fontSize: 64,
  minFontSize: 28,
  wide: 1.5,
  /** A direction badge's title: up to two lines (docs/04, Direction hotspots). */
  directionLines: 2,
} as const;

/**
 * A label of up to `maxLines` lines (docs/04: a direction badge's title shows on up to two lines, letters
 * about 1.8 degrees high): the largest letters from 64 px down at which the text wraps into `maxLines` lines
 * or fewer; still longer at the smallest letters, its first `maxLines` lines, the last ending in "…".
 */
export function labelLines(
  text: string,
  maxLines: number,
  measure: MeasureText,
  maxWidth: number = LABEL_TEXT.maxWidth,
): FittedText {
  const at = (size: number): FittedText =>
    fitText(
      text,
      {
        maxWidth,
        maxHeight: Infinity,
        fontSize: size,
        minFontSize: size,
        bold: true,
      },
      measure,
    );
  for (let size: number = LABEL_TEXT.fontSize; size >= LABEL_TEXT.minFontSize; size -= 2) {
    const fitted = at(size);
    if (fitted.lines.length <= maxLines) return fitted;
  }
  const kept = at(LABEL_TEXT.minFontSize).lines.slice(0, maxLines);
  kept[kept.length - 1] = `${kept[kept.length - 1] ?? ''}…`;
  return { lines: kept, fontSize: LABEL_TEXT.minFontSize };
}
